/**
 * TenantContext - Multi-tenant state management
 *
 * Manages tenant configuration, branding, and domain-based routing.
 * Automatically loads tenant configuration on mount and applies branding.
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { tenantService, TenantConfig, TenantConfigError } from '@/services/tenant';

/**
 * Tenant context state shape
 */
interface TenantContextState {
  tenant: TenantConfig | null;
  loading: boolean;
  error: string | null;
  loadTenant: () => Promise<void>;
  refreshTenant: () => Promise<void>;
  clearTenant: () => void;
}

/**
 * Create context with undefined default (requires provider)
 */
const TenantContext = createContext<TenantContextState | undefined>(undefined);

/**
 * TenantProvider props
 */
interface TenantProviderProps {
  children: ReactNode;
}

/**
 * TenantProvider component
 *
 * Wraps the application to provide tenant configuration to all child components.
 * Automatically fetches tenant configuration on mount based on current domain.
 */
export const TenantProvider: React.FC<TenantProviderProps> = ({ children }) => {
  const [tenant, setTenant] = useState<TenantConfig | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Load tenant configuration from backend
   * Handles errors and applies branding on success
   */
  const loadTenant = async (): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      // Fetch tenant configuration based on current domain
      const config = await tenantService.getTenantConfig();

      // Update state with tenant configuration
      setTenant(config);

      // Apply branding to the DOM
      tenantService.applyBranding(config);
    } catch (err) {
      // Handle tenant configuration errors
      const errorMessage =
        err instanceof TenantConfigError
          ? err.message
          : err instanceof Error
          ? err.message
          : 'Failed to load tenant configuration';

      // For platform domains, don't set error - just allow landing page to render
      if (err instanceof TenantConfigError && err.message.includes('Platform domain')) {
        setTenant(null);
        setError(null);
      } else {
        // For actual errors, set error state to show error screen
        setError(errorMessage);
        setTenant(null);
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Refresh tenant configuration
   * Clears cache and reloads from backend
   */
  const refreshTenant = async (): Promise<void> => {
    // Clear cached configuration
    tenantService.clearCache();

    // Reload tenant configuration
    await loadTenant();
  };

  /**
   * Clear tenant configuration
   * Used for cleanup or tenant switching
   */
  const clearTenant = (): void => {
    setTenant(null);
    setError(null);
    tenantService.clearCache();
  };

  /**
   * Load tenant configuration on component mount
   */
  useEffect(() => {
    loadTenant();
  }, []); // Empty dependency array - run once on mount

  /**
   * Context value provided to consumers
   */
  const value: TenantContextState = {
    tenant,
    loading,
    error,
    loadTenant,
    refreshTenant,
    clearTenant,
  };

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
};

/**
 * useTenant hook
 *
 * Custom hook for accessing tenant context in components.
 * Throws error if used outside of TenantProvider.
 *
 * @returns Tenant context state and methods
 * @throws Error if used outside TenantProvider
 *
 * @example
 * ```tsx
 * const { tenant, loading, error } = useTenant();
 *
 * if (loading) return <LoadingSpinner />;
 * if (error) return <ErrorMessage message={error} />;
 * if (!tenant) return <TenantNotFound />;
 *
 * return <Gallery photographerId={tenant.photographerId} />;
 * ```
 */
export const useTenant = (): TenantContextState => {
  const context = useContext(TenantContext);

  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }

  return context;
};

/**
 * Export context for advanced use cases (testing, etc.)
 */
export default TenantContext;
